import { useQuery } from "@apollo/client/react";
import {
  GetCommunicationssDocument,
  GetCommunicationssQuery,
  GetCommunicationssQueryVariables,
  Communications,
  CommunicationsFilter,
} from "@/src/gql/graphql";

export const useCommunicationsForUser = (
  userId: string,
  rangeStart?: Date,
  rangeEnd?: Date,
  options?: Omit<GetCommunicationssQueryVariables, "filter">
) => {
  const first = options?.first ?? 200;
  const userFilter = userId ? ({ user_id: { eq: userId } } satisfies CommunicationsFilter) : undefined;
  const dateFilter =
    rangeStart || rangeEnd
      ? ({
          occurred_at: {
            ...(rangeStart ? { gte: rangeStart.toISOString() } : {}),
            ...(rangeEnd ? { lte: rangeEnd.toISOString() } : {}),
          },
        } satisfies CommunicationsFilter)
      : undefined;

  const filter: CommunicationsFilter | undefined =
    userFilter && dateFilter
      ? {
          and: [userFilter, dateFilter],
        }
      : userFilter || dateFilter;

  const result = useQuery<GetCommunicationssQuery>(GetCommunicationssDocument, {
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    variables: {
      first,
      filter,
      ...options,
    },
  });

  const nodes = (result.data?.communicationsCollection?.edges
    ?.map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => node != null) || []) as Communications[];

  return {
    data: nodes,
    totalCount: result.data?.communicationsCollection?.totalCount ?? nodes.length,
    pageInfo: result.data?.communicationsCollection?.pageInfo,
    hasNextPage: result.data?.communicationsCollection?.pageInfo?.hasNextPage ?? false,
    fetchNextPage: async () => {
      const endCursor = result.data?.communicationsCollection?.pageInfo?.endCursor;
      const hasNext = result.data?.communicationsCollection?.pageInfo?.hasNextPage;
      if (!hasNext || !endCursor) {
        return null;
      }

      return result.fetchMore({
        variables: {
          ...options,
          first,
          after: endCursor,
          filter,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.communicationsCollection) {
            return previousResult;
          }

          const previousEdges = previousResult.communicationsCollection?.edges ?? [];
          const nextEdges = fetchMoreResult.communicationsCollection?.edges ?? [];

          return {
            ...fetchMoreResult,
            communicationsCollection: {
              ...fetchMoreResult.communicationsCollection,
              edges: [...previousEdges, ...nextEdges],
            },
          };
        },
      });
    },
    isLoading: result.loading,
    error: result.error || null,
    refetch: result.refetch,
  };
};

export const useCommunicationsForContact = (contactId: string) => {
  const result = useQuery<GetCommunicationssQuery>(GetCommunicationssDocument, {
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        contact_id: { eq: contactId },
      },
    },
  });

  const nodes = (result.data?.communicationsCollection?.edges
    ?.map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => node != null) || []) as Communications[];

  return {
    data: nodes,
    isLoading: result.loading,
    error: result.error || null,
    refetch: result.refetch,
  };
};
